import type { CampaignManagementApi } from '~/services/campaign-management-api/campaign-management-api.types';

import type { CampaignStatus } from './types';

import { HIGH_REVALIDATION_PCT_THRESHOLD } from './constants';

const TARGET_REJECTION_MESSAGES: Record<string, string> = {
  OVERLAPPING_ACTIVE_CAMPAIGN:
    'Já está em outra campanha ativa ou agendada no período.',
};

const DEFAULT_TARGET_REJECTION_MESSAGE = 'Não foi possível incluir este parceiro na campanha.';

export function formatTargetRejectionError(
  error: CampaignManagementApi.CreateCampaignTargetError,
): string {
  return TARGET_REJECTION_MESSAGES[error.code] ?? DEFAULT_TARGET_REJECTION_MESSAGE;
}

export function formatTargetRejectionReason(
  errors: CampaignManagementApi.CreateCampaignTargetError[],
): string {
  if (errors.length === 0) {
    return TARGET_REJECTION_MESSAGES.OVERLAPPING_ACTIVE_CAMPAIGN;
  }

  return [...new Set(errors.map(formatTargetRejectionError))].join(' ');
}

export const STATUS_LABELS: Record<CampaignStatus, string> = {
  active: 'Ativa',
  canceled: 'Cancelada',
  draft: 'Rascunho',
  finished: 'Encerrada',
  paused: 'Pausada',
  processing: 'Processando',
  scheduled: 'Agendada',
};

export interface CampaignStatusStyle {
  bg: string;
  color: string;
  dot: string;
}

export const STATUS_STYLES: Record<CampaignStatus, CampaignStatusStyle> = {
  active: { bg: '#e6f8f0', color: '#047857', dot: '#00b569' },
  canceled: { bg: '#fee2e2', color: '#b91c1c', dot: '#ef4444' },
  draft: { bg: '#f5f6f8', color: '#6b7280', dot: '#9ca3af' },
  finished: { bg: '#f5f6f8', color: '#6b7280', dot: '#9ca3af' },
  paused: { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  processing: { bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
  scheduled: { bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
};

export function formatTargetName(targetName?: null | string): string {
  const name = targetName?.trim();
  return name || '—';
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const UTC_DATE_BOUNDARY_RE = /^(\d{4})-(\d{2})-(\d{2})T(?:00:00:00|23:59:59)(?:\.\d+)?Z$/;

export function formatCampaignDate(value: null | string | undefined): string {
  if (!value) return '—';

  const localDate = parseLocalDateString(value);
  if (localDate) {
    return localDate.toLocaleDateString('pt-BR');
  }

  const boundaryMatch = UTC_DATE_BOUNDARY_RE.exec(value);
  if (boundaryMatch) {
    return formatLocalDateParts(
      Number(boundaryMatch[1]),
      Number(boundaryMatch[2]),
      Number(boundaryMatch[3]),
    );
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';

  return parsed.toLocaleDateString('pt-BR');
}

export function formatNumber(value: null | number): string {
  if (value === null) return '—';
  return value.toLocaleString('pt-BR');
}

export function formatPct(value: null | number): string {
  if (value === null) return '—';
  return `${value.toFixed(1)}%`;
}

export function isHighPct(value: null | number): boolean {
  return value !== null && value >= HIGH_REVALIDATION_PCT_THRESHOLD;
}

export function parseLocalDateString(value: string): Date | null {
  const match = DATE_ONLY_RE.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

export function formatLocalDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function getMinCampaignStartDate(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return formatLocalDateInputValue(tomorrow);
}

export function isCampaignStartDateValid(startDate: string): boolean {
  const parsed = parseLocalDateString(startDate);
  const minDate = parseLocalDateString(getMinCampaignStartDate());

  if (!parsed || !minDate) return false;

  return parsed.getTime() >= minDate.getTime();
}

export function toCampaignPeriodBoundaryIso(
  dateStr: string,
  _boundary: 'end' | 'start',
): string {
  const parsed = parseLocalDateString(dateStr);
  if (!parsed) return dateStr;

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatLocalDateParts(year: number, month: number, day: number): string {
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
}
